import { Icon } from '@iconify/react'
import { Link, useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import { Seo } from '@/seo/Seo'
import { useBranding } from '@/branding/BrandingProvider'
import { api, ApiError } from '@/api/client'

/**
 * Step 2 of password recovery: the destination of the emailed link
 * (`<brand origin>/reset-password?token=…`, built in admin-backend
 * `mailer.js#sendForgotPassword`). It POSTs to `/api/auth/reset-password`, which
 * validates the token and sets the new Keycloak password.
 *
 * This route did not exist before, so every reset link — including the ones the
 * admin console's "Send reset link" action sends — landed on react-router's
 * "Unexpected Application Error! 404 Not Found" page and no one could complete a
 * reset. Client-only: not prerendered, noindex (the URL carries a secret token).
 */

// Mirrors the express-validator chain on POST /auth/reset-password, so the user
// sees the requirements before the server rejects them.
const PASSWORD_RULES = [
  { test: (v: string) => v.length >= 8, label: 'At least 8 characters' },
  { test: (v: string) => /[A-Z]/.test(v), label: 'One uppercase letter' },
  { test: (v: string) => /[a-z]/.test(v), label: 'One lowercase letter' },
  { test: (v: string) => /[0-9]/.test(v), label: 'One number' },
  { test: (v: string) => /[^A-Za-z0-9]/.test(v), label: 'One special character' },
  { test: (v: string) => v.length > 0 && v === v.trim(), label: 'No leading or trailing spaces' },
]

export function ResetPasswordPage() {
  const { siteName } = useBranding()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [expired, setExpired] = useState(false)
  const [loading, setLoading] = useState(false)

  const ruleResults = PASSWORD_RULES.map((r) => ({ ...r, pass: r.test(password) }))
  const allRulesPass = ruleResults.every((r) => r.pass)
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0
  const canSubmit = allRulesPass && passwordsMatch && !loading

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setError('')
    setExpired(false)
    setLoading(true)
    try {
      await api.post('/auth/reset-password', { token, password, confirmPassword })
      setSuccess(true)
    } catch (err) {
      // ApiError carries the backend body; surface its specific message rather
      // than a generic failure so the user knows whether to request a new link.
      let detail = ''
      let fieldErrors = ''
      if (err instanceof ApiError) {
        try {
          const parsed = JSON.parse(err.body || '{}')
          detail = parsed?.error || ''
          if (Array.isArray(parsed?.errors)) {
            fieldErrors = parsed.errors.map((x: { msg?: string }) => x.msg).filter(Boolean).join('. ')
          }
        } catch {
          detail = err.body || ''
        }
        if (err.status === 400 && /unknown token/i.test(detail)) {
          setExpired(true)
          setError('This reset link is invalid or has already been used.')
        } else if (err.status === 400 && /unknown user/i.test(detail)) {
          setError('We could not find an account for this reset link.')
        } else {
          setError(fieldErrors || detail || 'Failed to reset your password. Please try again.')
        }
      } else {
        setError('Unable to reach the server. Please try again in a moment.')
      }
    } finally {
      setLoading(false)
    }
  }

  const shell = (children: React.ReactNode) => (
    <main className="min-h-screen flex items-center justify-center bg-white font-sans px-6 py-12">
      <Seo
        title={`Set a New Password — ${siteName}`}
        description={`Choose a new password for your ${siteName} account.`}
        path="/reset-password"
        noindex
      />
      <div className="w-full max-w-md">{children}</div>
    </main>
  )

  // Link opened without a token (hand-typed, or the mail client mangled the URL).
  if (!token) {
    return shell(
      <div>
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-6">
          <Icon icon="solar:danger-triangle-linear" className="text-2xl text-red-600" />
        </div>
        <h1 className="text-3xl font-medium tracking-tight text-navy mb-2">Invalid reset link</h1>
        <p className="text-slate-500 font-light leading-relaxed mb-6">
          This link is missing its reset token. Request a fresh one and use the newest email.
        </p>
        <Link
          to="/forgot-password"
          className="inline-block bg-navy text-white px-6 py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors shadow-sm"
        >
          Request a new link
        </Link>
      </div>,
    )
  }

  if (success) {
    return shell(
      <div>
        <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-6">
          <Icon icon="solar:check-circle-linear" className="text-2xl text-green-600" />
        </div>
        <h1 className="text-3xl font-medium tracking-tight text-navy mb-2">Password updated</h1>
        <p className="text-slate-500 font-light leading-relaxed mb-6">
          Your password has been changed. You can now sign in with it here or in the mobile app.
        </p>
        <Link
          to="/sign-in"
          className="inline-block bg-navy text-white px-6 py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors shadow-sm"
        >
          Sign In
        </Link>
      </div>,
    )
  }

  return shell(
    <>
      <Link
        to="/sign-in"
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy mb-12 transition-colors"
      >
        <Icon icon="solar:arrow-left-linear" /> Back to Sign In
      </Link>

      <div className="mb-10">
        <h1 className="text-3xl font-medium tracking-tight text-navy mb-2">Set a new password</h1>
        <p className="text-slate-500 font-light">Choose a strong password you don&apos;t use anywhere else.</p>
      </div>

      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
          {expired && (
            <Link to="/forgot-password" className="block mt-2 font-medium text-rust hover:text-navy transition-colors">
              Request a new link
            </Link>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
            New password
          </label>
          <input
            id="password"
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter a new password"
            className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
          />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-1">
            Confirm password
          </label>
          <input
            id="confirmPassword"
            type="password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter the new password"
            className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
          />
          {confirmPassword && !passwordsMatch && (
            <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
          )}
        </div>

        <div className="p-4 bg-slate-50 rounded-lg">
          <p className="text-xs font-medium text-slate-600 mb-2">Password requirements</p>
          <ul className="space-y-1">
            {ruleResults.map((r) => (
              <li
                key={r.label}
                className={`text-xs flex items-center gap-1.5 ${r.pass ? 'text-green-600' : 'text-slate-400'}`}
              >
                <Icon icon={r.pass ? 'solar:check-circle-bold' : 'solar:record-linear'} />
                {r.label}
              </li>
            ))}
          </ul>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full bg-navy text-white py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Updating...' : 'Update Password'}
        </button>
      </form>
    </>,
  )
}
